/**
 * Panel de resumen con totales generales por estado de todas las rutas
 */

import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OrderStates from '../../../../utils/OrderStates';

const ResumenEstados = ({ deliveries, compact = false }) => {
    // Calcular totales sumando todas las rutas
    const totales = deliveries.reduce((acc, delivery) => {
        acc.sinPreparar += delivery.sinPreparar?.total ?? 0;
        acc.enPreparacion += delivery.enPreparacion?.total ?? 0;
        acc.preparado += delivery.preparado?.total ?? 0;
        acc.enRevision += delivery.enRevision?.total ?? 0;
        acc.total += delivery.total ?? 0;
        return acc;
    }, {
        sinPreparar: 0,
        enPreparacion: 0,
        preparado: 0,
        enRevision: 0,
        total: 0
    });

    // Configuración de estadísticas (orden: sin preparar, en preparación, en revisión, preparado)
    const estadisticas = [
        {
            label: OrderStates.states[0],
            value: totales.sinPreparar,
            color: '#dc2626',
            bgColor: '#fee2e2',
            icon: <PendingActionsIcon sx={{ fontSize: 40 }} />
        },
        {
            label: OrderStates.states[1],
            value: totales.enPreparacion,
            color: '#2563eb',
            bgColor: '#dbeafe',
            icon: <PendingActionsIcon sx={{ fontSize: 40 }} />
        },
        {
            label: OrderStates.states[3],
            value: totales.enRevision,
            color: '#ea580c',
            bgColor: '#ffedd5',
            icon: <RateReviewIcon sx={{ fontSize: 40 }} />
        },
        {
            label: OrderStates.states[2],
            value: totales.preparado,
            color: '#16a34a',
            bgColor: '#dcfce7',
            icon: <CheckCircleIcon sx={{ fontSize: 40 }} />
        },
        {
            label: 'Total General',
            value: totales.total,
            color: '#cc071e',
            bgColor: '#fee2e2',
            icon: <AssignmentIcon sx={{ fontSize: 40 }} />
        }
    ];

    return (
        <Box>
            <Grid container spacing={compact ? 1.5 : 3}>
                {estadisticas.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={2.4} key={index}>
                        <Paper
                            elevation={0}
                            sx={{
                                position: 'relative',
                                px: compact ? 2 : 3,
                                py: compact ? 1.5 : 3,
                                backgroundColor: '#ffffff',
                                border: `2px solid ${stat.color}30`,
                                borderRadius: 3,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Barra lateral de color */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 4,
                                    background: stat.color
                                }}
                            />

                            {/* Contenido */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1.5 : 2 }}>
                                {/* Icono en círculo */}
                                <Box
                                    sx={{
                                        width: compact ? 60 : 70,
                                        height: compact ? 60 : 70,
                                        borderRadius: '50%',
                                        background: stat.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: `0 4px 12px ${stat.color}40`
                                    }}
                                >
                                    {React.cloneElement(stat.icon, {
                                        sx: {
                                            fontSize: compact ? 32 : 40,
                                            color: 'white'
                                        }
                                    })}
                                </Box>

                                {/* Números y label */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: stat.color,
                                            fontWeight: 800,
                                            fontSize: compact ? '2.5rem' : '3rem',
                                            lineHeight: 1,
                                            mb: compact ? 0.25 : 0.5
                                        }}
                                    >
                                        {stat.value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#495057',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            fontSize: compact ? '1.1rem' : '1.3rem',
                                            letterSpacing: 0.75,
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {stat.label}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ResumenEstados;
